import { type IPeerParticipant, type IPeerThreadSummary } from '../interface/peer-chat';

// Pure helpers for a parent's Messages inbox. The rows are AGENTS, never threads: an agent who
// has never been messaged has no thread and is exactly the person a parent needs to reach.

/** Which of the two participants is not the reader. */
export const counterpartOf = (summary: IPeerThreadSummary, me: number): IPeerParticipant =>
	summary.counterpart ?? (summary.parent.coreUserId === me ? summary.child : summary.parent);

/** The state of the counterpart's unit, as the PU list already labels it. */
export type InboxUnitState = 'pending' | 'conflict' | 'uploaded' | 'unknown';

/** What `sortInboxRows` needs to know about a row; the rest is presentation. */
export interface IInboxSortable {
	unread: number;
	unitState: InboxUnitState;
	lastMessageAt?: string | null;
	/** The counterpart's unit code, the final tiebreak so order is stable between polls. */
	code: string;
}

const UNIT_STATE_RANK: Record<InboxUnitState, number> = { pending: 0, conflict: 1, uploaded: 2, unknown: 3 };

const timeOf = (iso?: string | null): number => {
	if (!iso) return 0;
	const t = Date.parse(iso);
	return Number.isFinite(t) ? t : 0;
};

/**
 * unread desc → unit still pending, then conflict, then uploaded → latest activity → code.
 *
 * <p>"Still pending" above "uploaded" is the whole point: on election day the parent's eye
 * must land on the units that have not come in, not on the ones already done.</p>
 */
export const sortInboxRows = <T extends IInboxSortable>(rows: T[]): T[] =>
	[...rows].sort((a, b) => {
		if (a.unread !== b.unread) return b.unread - a.unread;
		const rank = UNIT_STATE_RANK[a.unitState] - UNIT_STATE_RANK[b.unitState];
		if (rank !== 0) return rank;
		const t = timeOf(b.lastMessageAt) - timeOf(a.lastMessageAt);
		if (t !== 0) return t;
		return a.code.localeCompare(b.code, undefined, { numeric: true });
	});

/** Max length of the last-line preview; the server trims to 140 on reply quotes, mirror it. */
const PREVIEW_MAX = 140;

/**
 * The one-line "last message" for an inbox row.
 *
 * <p>"You: " when the reader wrote it, so a parent scanning the list can tell a question
 * they asked from an answer they have not read.</p>
 */
export const previewOf = (summary: IPeerThreadSummary, me: number): string => {
	const raw = (summary.lastMessagePreview ?? '').trim();
	if (!raw) return '';
	const text = raw.length > PREVIEW_MAX ? `${raw.slice(0, PREVIEW_MAX - 1)}…` : raw;
	const mine =
		summary.lastMessageByCoreUserId != null
			? summary.lastMessageByCoreUserId === me
			: summary.lastMessageFrom != null && summary.lastMessageFrom === roleOf(summary, me);
	return mine ? `You: ${text}` : text;
};

/** The reader's role in a thread. */
export const roleOf = (summary: IPeerThreadSummary, me: number): 'parent' | 'child' | null => {
	if (summary.parent.coreUserId === me) return 'parent';
	if (summary.child.coreUserId === me) return 'child';
	return null;
};
